import React, { useEffect, useMemo, useState } from 'react';

import PageWrapper from '../PageWrapper';
import Page from '../Page';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';

import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  BubbleAccordion,
  BubbleAlert,
  BubbleBadge,
  BubbleButton,
  BubbleCheckbox,
  BubbleCheckboxGroup,
  BubbleDebounce,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleInput,
  BubbleOverlay,
  BubbleProgressBar,
  BubbleTable,
  BubbleTypeahead,
} from '@go-client/components/BubbleUI';
import ReserveNavigation from '@go-client/components/Navigations/ReserveNavigation';
import ReserveSubNavigation from '@go-client/components/Navigations/ReserveSubNavigation';
import ReservationPath, {
  ReservationFormMode,
} from '@go-client/components/ReservationPath/ReservationPath';
import { ReservableEventTemplateSnapshot } from '@go-shared/types/reservable';
import {
  Reservation,
  ReservationAnalytics,
  ReservationAnalyticsTallyItem,
  ReservationAttendance,
  ReservationStatus,
} from '@go-shared/types/reservation';
import {
  ReserveTimeline,
  ReserveTimelineEvent,
  ReserveTimelineItem,
} from '@go-shared/types/reserve';
import {
  toggleStatus,
  findOngoing,
  get as getReservation,
  changeAttendance,
} from '@go-client/lib/api/reservation';
import { toggleSlotException } from '@go-client/lib/api/reservable';
import {
  addDaysToDateUTC,
  addMinutesToTime,
  DEFAULT_TIMESTR_FORMAT,
  dateToIsoUTC,
  isoToDateLocal,
  dateToIsoLocal,
  timeCompare,
  timeToString,
  isoToDateUTC,
  startOfDayUTC,
  isIsoDate,
  formatTimeRange,
} from '@go-shared/lib/time';
import { getHourFormat } from '@go-shared/lib/property';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import {
  hashStringToColor,
  hasOwnProperty,
  makeFullName,
  safeIdCompare,
  splitEverySecondWord,
  formatCentsToUSD,
} from '@go-shared/lib/utils';
import { firstOf, groupBy, mapBy, notUndefined, onlyUnique } from '@go-shared/lib/functional';
import { getUsers } from '@go-client/lib/api/user';
import { User } from '@go-shared/types/user';
import qslib from '@go-shared/lib/querystring';
import { BareEventTemplate } from '@go-shared/types/eventTemplate';
import { BubbleBadgeProps } from '@go-client/components/BubbleUI/BubbleBadge';
import {
  buildHostTimeSlotKeyPart,
  buildResourceSlotKeyPart,
  calculateReservationAnalytics,
  DEFAULT_RESOURCE_KEY,
  slotKeyWithEvent,
} from '@go-shared/lib/reserve';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import { Time } from '@go-shared/types/time';
import {
  makeReservationTableFields,
  makeReservationTableItem,
  makeReservationTableTemplates,
} from './ReserveTransactions';
import {
  canCreateSomeReservations,
  canHostEvents,
  canManageAllReservations,
  canManageReservation,
  canManageTimelineItem,
} from '@go-shared/lib/permissions';

const DEFAULT_ONGOING_DATE = dateToIsoLocal();
const DEFAULT_ONGOING_DAYS_BEFORE = 0;
const DEFAULT_ONGOING_DAYS_AFTER = 14;

interface ReserveOngoingParams {
  property: string;
}

interface ListViewTableItem {
  id: string;
  eventTitle: string;
  date: string;
  label?: string;
  startTime: string;
  endTime?: string;
  host?: string;
  resource?: string;
  reservationCount: number;
  capacity: number;
  seatsTaken: number;
  directSeatsTaken: number;
  indirectSeatsTaken: number;
  isResource: boolean;
  isExceptionDate: boolean;
  allowOverfill: boolean;

  color: string;
  isFirstRow: boolean;
  isLastRow: boolean;
  totalNumRows: number;
  isExpandedRow: boolean;
  hasExpandedRow: boolean;
  reservations: ReservationTableItem[];
  analytics: ReservationAnalytics;

  _slot: ReserveTimelineItem;
  _firstSlot: ReserveTimelineItem;
  _event: BareEventTemplate;
  _resourceMatrix?: ResourceMatrix;
}

interface GridViewTableItem {
  id: string;
  time: string;
  [key: string]: ListViewTableItem | string | undefined;
}

function gridViewCellIsListViewTableItem(
  cell: ListViewTableItem | string | undefined
): cell is ListViewTableItem {
  return typeof cell === 'object' && cell !== null && 'id' in cell;
}

export interface ReservationTableItem<T extends Reservation = Reservation> {
  id: string;
  event: string;
  date: string;
  startTime: string;
  endTime?: string;
  host?: string;
  resource?: string;
  status: ReservationStatus;
  cartCode: string;
  reservationCode: string;
  name: string;
  email: string;
  phone: string;
  statusVariant: BubbleBadgeProps['variant'];
  attendance?: ReservationAttendance;
  attendanceVariant: BubbleBadgeProps['variant'];
  isIndirect: boolean;
  isCancelled: boolean;
  isOccurrence: boolean;
  sourceReservationId: string;
  analytics: ReservationAnalytics;
  _reservation: T;
  _event: ReservableEventTemplateSnapshot;
}

export interface ReservationTallyItem {
  id: string;
  name: string;
  total: number;
  typeTotal: number;
  isExpanded?: boolean;
  _tally: ReservationAnalyticsTallyItem;
}

interface ResourceMatrixItem {
  id: string;
  time: string;
  timeLabel?: string;
  host?: string;
  isExceptionDate: boolean;
  slot?: ReserveTimelineItem;
  cells: Record<string, ResourceMatrixCell>;
}

interface ResourceMatrixCell {
  description: string;
  seatsTaken: number;
  directSeatsTaken: number;
  indirectSeatsTaken: number;
  allowOverfill: boolean;
  capacity: number;
  time: Time;
  duration?: number;
  slot?: ReserveTimelineItem;
  isExceptionDate: boolean;
  reservations: ReservationTableItem[];
}

interface ResourceMatrix {
  columnOpts: Array<{ key: string; label: string }>;
  rowOpts: Array<{
    key: string;
    label: string;
    timeLabel?: string;
    hostLabel?: string;
    host?: string;
    isExceptionDate: boolean;
  }>;
  cellMatrix: Record<string, Record<string, ResourceMatrixCell>>;
  summaryCapacity: number;
  summarySeatsTaken: number;
  summaryAllowOverfill: boolean;
  allReservations: ReservationTableItem[];
}

interface ResourceMatrixCellCoord {
  rowKey: string;
  colKey: string;
}

interface ListViewTableItemGroup {
  id: string;
  date: string;
  color: string;
  items: ListViewTableItem[];
}

interface GridViewTableItemGroup {
  id: string;
  name: string;
  color: string;
  items: GridViewTableItem[];
}

interface OngoingFilters {
  selectedExperiences: Array<BubbleTypeaheadValue<BareEventTemplate>>;
  selectedHosts: Array<BubbleTypeaheadValue<User | null>>;
  groupByEvent: boolean;
  daysBySide: boolean;
  hideEmptyDays: boolean;
  hideEmptySlots: boolean;
  selectedDayOnly: boolean;
  showArchived: boolean;
  showIndirect: boolean;
}

type ListViewTableFields = BubbleTableFields<ListViewTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

type GridViewTableFields = BubbleTableFields<GridViewTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

export type ReservationTableFields = BubbleTableFields<ReservationTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

function makeDefaultFilters(user: User) {
  const selectedHosts: Array<BubbleTypeaheadValue<User>> = [];

  if (canHostEvents(user)) {
    selectedHosts.push({ key: user._id, item: user, refKey: user._id });
  }

  const filters: OngoingFilters = {
    selectedExperiences: [],
    selectedHosts,
    groupByEvent: true,
    daysBySide: false,
    hideEmptyDays: true,
    hideEmptySlots: false,
    selectedDayOnly: false,
    showArchived: false,
    showIndirect: true,
  };

  return filters;
}
interface OngoingQueryStrings {
  date: string;
  daysBefore: number;
  daysAfter: number;
  reservation?: string;
}

function buildResourceMatrix(groupedItems: ListViewTableItem[]): ResourceMatrix {
  const columnOpts: ResourceMatrix['columnOpts'] = [];
  const rowOpts: ResourceMatrix['rowOpts'] = [];
  const cellMatrix: ResourceMatrix['cellMatrix'] = {};
  const allReservations = groupedItems.flatMap(item => item.reservations);

  let summaryCapacity = 0;
  let summarySeatsTaken = 0;
  let summaryAllowOverfill = true;

  groupedItems.forEach(itm => {
    const { host, resource, time, duration } = itm._slot;

    const colKey = resource || DEFAULT_RESOURCE_KEY;
    const colLabel = itm.resource || 'Unknown';

    const rowKey = buildHostTimeSlotKeyPart(host, time, duration);
    const rowLabel = formatTimeRange(time, duration);

    if (!columnOpts.some(c => c.key === colKey)) {
      columnOpts.push({ key: colKey, label: colLabel });
    }

    if (!rowOpts.some(r => r.key === rowKey)) {
      rowOpts.push({
        key: rowKey,
        label: rowLabel,
        timeLabel: itm.label,
        hostLabel: itm.host,
        host,
        isExceptionDate: itm.isExceptionDate,
      });
    }

    if (!cellMatrix[rowKey]) {
      cellMatrix[rowKey] = {};
    }

    cellMatrix[rowKey][colKey] = {
      capacity: itm.capacity,
      seatsTaken: itm.seatsTaken,
      directSeatsTaken: itm.directSeatsTaken,
      indirectSeatsTaken: itm.indirectSeatsTaken,
      allowOverfill: itm.directSeatsTaken <= itm.capacity,
      time,
      duration,
      description: `${rowLabel} • ${colLabel}${itm.host ? ` • ${itm.host}` : ''}`,
      slot: itm._slot,
      isExceptionDate: itm.isExceptionDate,
      reservations: itm.reservations,
    };

    summaryCapacity += itm.capacity;
    summarySeatsTaken += itm.seatsTaken;
    summaryAllowOverfill = summaryAllowOverfill && cellMatrix[rowKey][colKey].allowOverfill;
  });

  return {
    columnOpts,
    rowOpts,
    cellMatrix,
    summaryCapacity,
    summarySeatsTaken,
    summaryAllowOverfill,
    allReservations,
  };
}

function getQueryStrings(): OngoingQueryStrings {
  const qs = qslib.readQs();

  const daysBefore =
    hasOwnProperty(qs, 'daysBefore') && typeof qs.daysBefore === 'number'
      ? qs.daysBefore
      : DEFAULT_ONGOING_DAYS_BEFORE;
  const daysAfter =
    hasOwnProperty(qs, 'daysAfter') && typeof qs.daysAfter === 'number'
      ? qs.daysAfter
      : DEFAULT_ONGOING_DAYS_AFTER;
  const date =
    hasOwnProperty(qs, 'date') && typeof qs.date === 'string' ? qs.date : DEFAULT_ONGOING_DATE;
  const reservation =
    hasOwnProperty(qs, 'reservation') && typeof qs.reservation === 'string'
      ? qs.reservation
      : undefined;

  return { daysBefore, daysAfter, date, reservation };
}

type ReserveOngoingProps = RouteComponentProps<ReserveOngoingParams>;

export default function ReserveOngoing(props: ReserveOngoingProps) {
  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();

  const [queryStrings, setQueryStrings] = useState<OngoingQueryStrings>(getQueryStrings());
  const [loading, setLoading] = useState(true);
  const [changingException, setChangingException] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [mode, _setMode] = useState<ReservationFormMode | undefined>(undefined);

  const [users, setUsers] = useState<User[]>([]);
  const [ongoings, setOngoings] = useState<ReserveTimeline[]>([]); // New state for schedules
  const [expandedRow, setExpandedRow] = useState<{ rowKey: string; date: string } | undefined>(
    undefined
  );
  const [filters, setFilters] = useState<OngoingFilters>(makeDefaultFilters(user));
  const [showDelete, setShowDelete] = useState<{ [key: string]: boolean }>({});
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  const [showMoreTicketDetails, setShowMoreTicketDetails] = useState(false);
  const [showMorePackageDetails, setShowMorePackageDetails] = useState(false);
  const [showMoreAddonDetails, setShowMoreAddonDetails] = useState(false);
  const [reservationSearch, setReservationSearch] = useState('');
  const [showReservationList, setShowReservationList] = useState(false);

  const [activeMatrixCellCoord, setActiveMatrixCellCoord] = useState<
    ResourceMatrixCellCoord | undefined
  >(undefined);

  const hourFormat = getHourFormat(activeProperty);
  const usersDict = mapBy(users, '_id');

  async function fetchData() {
    setLoading(true);

    const startDate = isIsoDate(queryStrings.date)
      ? isoToDateUTC(queryStrings.date)
      : startOfDayUTC();

    const sailingsFromDate = addDaysToDateUTC(startDate, -queryStrings.daysBefore);
    const sailingsToDate = addDaysToDateUTC(startDate, queryStrings.daysAfter);

    try {
      const [users, ongoings] = await Promise.all([
        getUsers(activeProperty._id),
        findOngoing(
          activeProperty._id,
          dateToIsoUTC(sailingsFromDate),
          dateToIsoUTC(sailingsToDate),
          filters.showArchived
        ),
      ]);

      setUsers(users.filter(canHostEvents));
      setOngoings(ongoings);
      refreshSelectedReservation().catch(console.error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData().catch(console.error);
  }, [queryStrings, activeProperty, filters.showArchived]);

  useEffect(() => {
    setActiveMatrixCellCoord(undefined);
    setShowReservationList(false);
  }, [expandedRow]);

  function toggleExpandedRow(rowKey: string, date: string) {
    setExpandedRow(prev =>
      prev?.rowKey === rowKey && prev?.date === date ? undefined : { rowKey: rowKey, date }
    );
  }

  async function refreshSelectedReservation() {
    const parsedQs = getQueryStrings();

    const toSelect = parsedQs.reservation;
    if (!toSelect) return;

    const reservation = await getReservation(activeProperty._id, toSelect);

    handleOpenReservationPanel({ type: 'edit', edit: reservation });
  }

  async function handleEditReservationById(id: string) {
    // Always fetch fresh from server so flattened occurrence facsimiles don't
    // bleed their overridden date/time into the edit form.
    qslib.writeQs({ reservation: id });
    await refreshSelectedReservation();
  }

  function handleOpenReservationPanel(newMode: ReservationFormMode) {
    _setMode(newMode);
    if (newMode.type === 'edit') {
      qslib.writeQs({ reservation: newMode.edit._id });
    }
  }

  function handleCloseReservationPanel() {
    _setMode(undefined);
    qslib.writeQs({});
  }

  function handleOnAddDaysBefore(amt: number) {
    const newQueryStrings = { ...queryStrings, daysBefore: queryStrings.daysBefore + amt };

    qslib.writeQs(newQueryStrings);

    setQueryStrings(newQueryStrings);
  }

  function handleOnAddDaysAfter(amt: number) {
    const newQueryStrings = { ...queryStrings, daysAfter: queryStrings.daysAfter + amt };

    qslib.writeQs(newQueryStrings);

    setQueryStrings(newQueryStrings);
  }

  async function handleOnChangeDate(iso: string) {
    const newQueryStrings = { ...queryStrings, date: iso };

    qslib.writeQs(newQueryStrings);

    setQueryStrings(newQueryStrings);
  }

  function buildResourcesDict(
    timelineEvent: ReserveTimelineEvent,
    timelineItems: ReserveTimelineItem[]
  ) {
    const resourcesDict: Record<string, string> = {};

    (timelineEvent.template.reservables?.tickets ?? []).forEach(ticket => {
      if (!resourcesDict[ticket._id]) {
        resourcesDict[ticket._id] = ticket.name;
      }
    });

    timelineItems.forEach(slot => {
      slot.reservations.forEach(res => {
        res.eventSnapshot.reservables.tickets.forEach(ticket => {
          if (!resourcesDict[ticket._id]) {
            resourcesDict[ticket._id] = ticket.name;
          }
        });
      });
    });

    return resourcesDict;
  }

  function makeListViewTableItem(
    timelineItems: ReserveTimelineItem[],
    timelineEvent: ReserveTimelineEvent,
    date: string,
    resourcesDict: Record<string, string>
  ): ListViewTableItem[] {
    const expandedItemIdx = timelineItems.findIndex(
      sl => slotKeyWithEvent(timelineEvent.template._id, sl.slotKey) === expandedRow?.rowKey
    );

    const activeFilter = (res: { status: string }) =>
      filters.showArchived || res.status !== 'cancelled';

    return timelineItems.map((slot, i) => {
      const directReservations = slot.reservations.filter(activeFilter);
      const indirectReservations = slot.overlaps.flatMap(skey => {
        const overlappingSlot = timelineItems.find(itm => itm.slotKey === skey);
        if (!overlappingSlot) return [];
        return overlappingSlot.reservations.filter(activeFilter);
      });

      const reservationsListing = filters.showIndirect
        ? [...directReservations, ...indirectReservations]
        : directReservations;

      const indirectReservationIds = new Set(indirectReservations.map(r => r._id));

      const directSeatsTaken = slot.reservations
        .filter(r => r.status !== 'cancelled')
        .map(r => r.tickets.length)
        .reduce((a, b) => a + b, 0);

      const indirectSeatsTaken = slot.overlaps
        .flatMap(skey => {
          const overlappingSlot = timelineItems.find(itm => itm.slotKey === skey);
          if (!overlappingSlot) return [];
          return overlappingSlot.reservations.filter(r => r.status !== 'cancelled');
        })
        .map(r => r.tickets.length)
        .reduce((a, b) => a + b, 0);

      const seatsTaken = directSeatsTaken + indirectSeatsTaken;

      const isExceptionDate =
        (slot.exceptions ?? []).findIndex(ex => ex.date === date && ex.resource === slot.resource) >
        -1;

      const item: ListViewTableItem = {
        id: slotKeyWithEvent(timelineEvent.template._id, slot.slotKey),
        isResource: false,
        eventTitle: timelineEvent.template.title,
        date,
        label: slot.label,
        reservationCount: slot.reservations.length,
        startTime: timeToString(slot.time, hourFormat, DEFAULT_TIMESTR_FORMAT),
        endTime: slot.duration
          ? timeToString(
              addMinutesToTime(slot.time, slot.duration),
              hourFormat,
              DEFAULT_TIMESTR_FORMAT
            )
          : undefined,
        reservations: reservationsListing.map(res =>
          makeReservationTableItem(res, usersDict, hourFormat, s =>
            indirectReservationIds.has(s._id)
          )
        ),
        host:
          slot.host && usersDict[slot.host]
            ? makeFullName(usersDict[slot.host].firstName, usersDict[slot.host].lastName)
            : undefined,
        resource: slot.resource ? resourcesDict[slot.resource] : undefined,
        capacity: isExceptionDate ? 0 : slot.capacity,
        seatsTaken,
        directSeatsTaken,
        indirectSeatsTaken,
        color: hashStringToColor(timelineEvent.template.title),
        isFirstRow: !filters.groupByEvent ? false : i === 0,
        isLastRow: !filters.groupByEvent ? false : i === timelineItems.length - 1,
        isExpandedRow: i === expandedItemIdx,
        isExceptionDate,
        totalNumRows: timelineItems.length,
        hasExpandedRow: expandedItemIdx > -1,
        allowOverfill: false,
        _firstSlot: timelineItems[0],
        _slot: slot,
        _event: timelineEvent.template,
        analytics: calculateReservationAnalytics(slot.reservations),
      };

      return item;
    });
  }

  function condenseListViewTableItems(
    timelineItems: ReserveTimelineItem[],
    timelineEvent: ReserveTimelineEvent,
    date: string,
    listViewItems: ListViewTableItem[]
  ): ListViewTableItem[] {
    // we need to condense all these slotRows into one row - when expanded, we have
    // a grid to show the individual slots and their resources/hosts. here we just build
    // a summary for the one row.

    const summaryId = `${timelineEvent.template._id}-${date}-summary`;
    const allReservations = listViewItems.flatMap(row => row.reservations);

    const matrix = buildResourceMatrix(listViewItems);

    const hostCount = listViewItems
      .map(row => row.host)
      .filter(notUndefined)
      .filter(onlyUnique).length;
    const timeCount = Object.keys(matrix.rowOpts).length;
    const resourcesCount = Object.keys(matrix.columnOpts).length;

    const summaryItem: ListViewTableItem = {
      id: summaryId,
      isResource: true,
      eventTitle: timelineEvent.template.title,
      date,
      label: timeCount > 0 ? `${timeCount} times` : undefined,
      startTime: '',
      reservationCount: listViewItems.reduce((acc, row) => acc + row.reservationCount, 0),
      reservations: allReservations,
      host: hostCount > 0 ? `${hostCount} hosts` : undefined,
      resource: resourcesCount > 0 ? `${resourcesCount} rentals` : undefined,
      capacity: matrix.summaryCapacity,
      seatsTaken: matrix.summarySeatsTaken,
      directSeatsTaken: 0,
      indirectSeatsTaken: 0, // no stripe resource progress bars at this lvl
      color: listViewItems[0].color,
      isFirstRow: filters.groupByEvent,
      isLastRow: filters.groupByEvent,
      isExpandedRow: expandedRow?.rowKey === summaryId,
      totalNumRows: 1,
      hasExpandedRow: expandedRow?.rowKey === summaryId,
      isExceptionDate: false,
      allowOverfill: matrix.summaryAllowOverfill,
      _firstSlot: timelineItems[0],
      _slot: timelineItems[0],
      _event: timelineEvent.template,
      analytics: calculateReservationAnalytics(
        listViewItems.flatMap(row => row._slot.reservations)
      ),
      _resourceMatrix: matrix,
    };

    return [summaryItem];
  }

  const listViewTableGroupItems: ListViewTableItemGroup[] = useMemo(() => {
    if (viewMode !== 'list') return [];

    const items = ongoings
      .map(ongoing => {
        if (filters.selectedDayOnly && ongoing.date !== queryStrings.date) {
          return undefined;
        }

        const dateObj = isoToDateLocal(ongoing.date);

        const oItem: ListViewTableItemGroup = {
          id: ongoing.date,
          date: dateObj.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            weekday: 'short',
          }),
          color: hashStringToColor(dateObj.toLocaleDateString('en-US', { weekday: 'short' })),
          items: ongoing.events
            .filter(ev =>
              filters.selectedExperiences.length > 0
                ? filters.selectedExperiences.some(se =>
                    safeIdCompare(ev.template._id, se.item._id)
                  )
                : true
            )
            .flatMap(ev => {
              const isResourceEvent = ev.template.reservables?.experienceType === 'resource';

              const items = ev.items
                .filter(s =>
                  filters.selectedHosts.length > 0
                    ? filters.selectedHosts.some(sh => safeIdCompare(sh.item?._id, s.host))
                    : true
                )
                .filter(s => (filters.hideEmptySlots ? s.reservations.length > 0 : true));

              const resourcesDict = buildResourcesDict(ev, items);
              const slotRows = makeListViewTableItem(items, ev, ongoing.date, resourcesDict);

              return isResourceEvent && slotRows.length > 0
                ? condenseListViewTableItems(items, ev, ongoing.date, slotRows)
                : slotRows;
            })
            // events are sorted within themselves, but not yet sorted against other events
            .sort((a, b) => timeCompare(a._firstSlot.time, b._firstSlot.time)),
        };

        if (!filters.groupByEvent) {
          // kind of sucks, but now I have to sort again if the user does not want it grouped by group
          oItem.items.sort((a, b) => timeCompare(a._slot.time, b._slot.time));
        }

        return filters.hideEmptyDays && oItem.items.length < 1 ? undefined : oItem;
      })
      .filter(notUndefined);

    return items;
  }, [ongoings, filters, expandedRow, queryStrings.date, viewMode]);

  const listViewTableFields: ListViewTableFields[] = [
    {
      key: 'event',
      _visible: filters.groupByEvent,
      behavior: BubbleTableFieldBehavior.MinWidth,
      tdProps: {
        className: 'pr-3-i',
      },
    },
    {
      key: 'eventTitle',
      _visible: !filters.groupByEvent,
      label: 'Event',
      tdProps: {
        style: { width: '300px' },
      },
    },
    {
      key: 'time',
    },
    {
      key: 'host',
      label: 'Rental / Host',
    },
    {
      key: 'reservationCount',
      label: 'Reservations',
    },
    { key: 'revenue' },
    { key: 'cancelled', label: '', behavior: BubbleTableFieldBehavior.Condense },
    { key: 'stats', behavior: BubbleTableFieldBehavior.Condense },
    {
      key: 'capacity',
      tdProps: {
        className: 'whitespace-nowrap',
        style: { width: '200px' },
      },
    },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
  ]
    .filter(notUndefined)
    .filter(tf => (tf._visible !== undefined ? tf._visible : true));

  const listViewTableTemplates: BubbleTableTemplate<ListViewTableItem>[] = [
    {
      key: 'event',
      render: ({ isFirstRow, eventTitle }) =>
        isFirstRow ? (
          <div className="flex flex-col gap-3">
            <div>
              {splitEverySecondWord(eventTitle).map((v, i) => (
                <div className="w-32 truncate" key={i}>
                  {v}
                </div>
              ))}
            </div>
          </div>
        ) : null,
      tdProps: ({ isFirstRow, totalNumRows, hasExpandedRow, color }, fieldProps) => {
        const classes = ['border-b-2 align-top text-md font-semibold text-white relative'];
        if (fieldProps.className) classes.push(fieldProps.className);

        return isFirstRow
          ? {
              rowSpan: totalNumRows + (hasExpandedRow ? 1 : 0),
              className: classes.join(' '),
              style: {
                backgroundColor: color,
                borderColor: color,
              },
            }
          : { style: { display: 'none' } };
      },
    },
    {
      key: 'eventTitle',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: ({ eventTitle, color }) => (
        <div className="font-semibold" style={{ color }}>
          {eventTitle}
        </div>
      ),
    },
    {
      key: 'time',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: ({ startTime, endTime, label }) => (
        <div>
          {label && <div>{label}</div>}
          <div className={`${label ? 'text-xs text-slate' : ''}`}>
            {startTime}
            <span className="whitespace-nowrap">{endTime ? ` - ${endTime}` : ''}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'host',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: ({ host, resource }) =>
        (host || resource) && (
          <div>
            {resource && <div>{resource}</div>}
            <div className={`${resource ? 'text-xs text-slate' : ''}`}>{host}</div>
          </div>
        ),
    },
    {
      key: 'reservationCount',
      tdProps: makeDefaultListViewTemplateTdProps,
    },
    {
      key: 'revenue',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: data => (
        <span
          className={`${data.analytics.totalRevenue === 0 ? '' : data.analytics.totalRevenue > 0 ? 'font-bold text-lime-darkest' : 'font-bold text-crimson'}`}
        >
          {formatCentsToUSD(data.analytics.totalRevenue)}
        </span>
      ),
    },
    {
      key: 'cancelled',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: data =>
        data.isExceptionDate ? (
          <BubbleBadge variant="crimson" className="mt-1">
            Cancelled
          </BubbleBadge>
        ) : (
          <></>
        ),
    },
    {
      key: 'stats',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: data => {
        return (
          <BubbleButton
            onClick={() => toggleExpandedRow(data.id, data.date)}
            variant="white"
            inverse
            className="p-0-i"
          >
            <BubbleBadge
              className="mt-1 mr-1 whitespace-nowrap-i"
              variant={data.analytics.numOfTickets > 0 ? 'lime' : 'smoke'}
            >
              <FontAwesomeIcon fixedWidth className="mr-1" icon="ticket" />
              <span>{data.analytics.numOfTickets}</span>
            </BubbleBadge>
            <BubbleBadge
              className="mt-1 mr-1 whitespace-nowrap-i"
              variant={data.analytics.numOfPackages > 0 ? 'banana' : 'smoke'}
            >
              <FontAwesomeIcon fixedWidth className="mr-1" icon="box-open" />
              <span>{data.analytics.numOfPackages}</span>
            </BubbleBadge>
            <BubbleBadge
              className="mt-1 mr-1 whitespace-nowrap-i"
              variant={data.analytics.numOfAddons > 0 ? 'ocean' : 'smoke'}
            >
              <FontAwesomeIcon fixedWidth className="mr-1" icon="puzzle-piece" />
              <span>{data.analytics.numOfAddons}</span>
            </BubbleBadge>
          </BubbleButton>
        );
      },
    },
    {
      key: 'capacity',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: ({ capacity, seatsTaken, indirectSeatsTaken, allowOverfill, isExpandedRow }) => {
        return (
          <BubbleProgressBar
            current={seatsTaken}
            max={capacity}
            striped={indirectSeatsTaken > 0}
            allowOverfill={allowOverfill}
            active={isExpandedRow}
          />
        );
      },
    },
    {
      key: 'actions',
      tdProps: makeDefaultListViewTemplateTdProps,
      render: data => (
        <BubbleOverlay
          loading={changingException}
          hideSpinner
          loadingClassName="rounded"
          className="text-right"
        >
          {!data.isResource && canManageTimelineItem(user, data._slot) && (
            <BubbleButton
              onClick={() => handleToggleException(data.date, data._event, data._slot)}
              className="p-2-i"
              variant={data.isExceptionDate ? 'crimson' : 'slate'}
              inverse
              icon="ban"
            />
          )}
          {canManageTimelineItem(user, data._slot) && (
            <BubbleButton
              onClick={() =>
                handleOpenReservationPanel({
                  type: 'create',
                  slot: !data.isResource ? data._slot : undefined,
                  event: data._event,
                  initialDate: data.date,
                })
              }
              className="p-2-i"
              variant="navy"
              inverse
              icon="plus"
            />
          )}
          <BubbleButton
            onClick={() => toggleExpandedRow(data.id, data.date)}
            className="p-2-i"
            variant="navy"
            inverse
            icon={
              expandedRow?.rowKey === data.id
                ? 'arrow-down-left-and-arrow-up-right-to-center'
                : 'arrow-up-right-and-arrow-down-left-from-center'
            }
          />
        </BubbleOverlay>
      ),
    },
  ];

  function makeDefaultListViewTemplateTdProps(
    data: ListViewTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const { isLastRow, isExpandedRow, color } = data;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        isLastRow && !isExpandedRow ? 'border-b-2' : undefined,
        isExpandedRow ? 'bg-bubblegum-lightest' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
      style: {
        ...(fieldTdProps.style || {}),
        borderBottomColor: isLastRow ? (color as string) : '',
      },
    };
  }

  const gridDates = ongoings.map(ongoing => ongoing.date);

  const gridViewTableGroupItems = useMemo(() => {
    if (viewMode !== 'grid') return [];

    const allEvents = firstOf(
      ongoings.flatMap(ongoing => ongoing.events),
      e => e.template._id
    ).sort((a, b) => a.template.title.localeCompare(b.template.title));

    // create a map that with a given event template id I can get a unique list
    // of all possible timeslots across all the days for that event template
    const eventsByTemplateId = groupBy(
      ongoings.flatMap(ongoing => ongoing.events),
      ev => ev.template._id
    );

    const timeslotsByTemplateId = mapBy(
      Object.entries(eventsByTemplateId).map(([eventTemplateId, events]) => {
        const slotGridKey = (slot: ReserveTimelineItem) =>
          buildResourceSlotKeyPart(
            {
              resource: slot.resource,
              host: slot.host,
            },
            slot.time,
            slot.duration
          );

        const uniqueTimeslots = Object.values(
          mapBy(
            events.flatMap(event => event.items),
            slotGridKey
          )
        ).sort((a, b) => timeCompare(a.time, b.time));

        return {
          eventTemplateId,
          timeslots: uniqueTimeslots,
        };
      }),
      'eventTemplateId'
    );

    const items = allEvents.map(ev => {
      const allTimeslotsOfEvent = timeslotsByTemplateId[ev.template._id]?.timeslots ?? [];
      const slotsForEvent = eventsByTemplateId[ev.template._id]?.flatMap(e => e.items) ?? [];
      const resourcesDict = buildResourcesDict(ev, allTimeslotsOfEvent);
      const slotGridKey = (slot: ReserveTimelineItem) =>
        buildResourceSlotKeyPart(
          {
            resource: slot.resource,
            host: slot.host,
          },
          slot.time,
          slot.duration
        );

      const sortedTimeslotsOfEvent = [...allTimeslotsOfEvent].sort((a, b) => {
        const hostA =
          a.host && usersDict[a.host]
            ? makeFullName(usersDict[a.host].firstName, usersDict[a.host].lastName)
            : '';
        const hostB =
          b.host && usersDict[b.host]
            ? makeFullName(usersDict[b.host].firstName, usersDict[b.host].lastName)
            : '';

        const hostComp = hostA.localeCompare(hostB);
        if (hostComp !== 0) return hostComp;

        const resourceA = a.resource ? resourcesDict[a.resource] || '' : '';
        const resourceB = b.resource ? resourcesDict[b.resource] || '' : '';

        const resourceComp = resourceA.localeCompare(resourceB);
        if (resourceComp !== 0) return resourceComp;

        const timeComp = timeCompare(a.time, b.time);
        if (timeComp !== 0) return timeComp;

        return (a.duration || 0) - (b.duration || 0);
      });

      const oItem: GridViewTableItemGroup = {
        id: ev.template._id,
        name: ev.template.title,
        color: hashStringToColor(ev.template.title),
        items: sortedTimeslotsOfEvent.map(slot => {
          const dateCells = gridDates.reduce<{ [key: string]: ListViewTableItem | undefined }>(
            (acc, date) => {
              const cell = slotsForEvent.find(
                s => slotGridKey(s) === slotGridKey(slot) && dateToIsoUTC(s.date) === date
              );
              if (cell) {
                const [item] = makeListViewTableItem([cell], ev, date, {});
                acc[date] = item;
              }
              return acc;
            },
            {}
          );

          return {
            id: `${ev.template._id}-${slotGridKey(slot)}`,
            time: formatTimeRange(slot.time, slot.duration),
            host:
              slot.host && usersDict[slot.host]
                ? makeFullName(usersDict[slot.host].firstName, usersDict[slot.host].lastName)
                : undefined,
            resource: slot.resource ? resourcesDict[slot.resource] : undefined,
            ...dateCells,
          };
        }),
      };

      return oItem;
    });

    return items;
  }, [ongoings, filters, gridDates, viewMode]);

  const activeDates = useMemo(() => {
    if (!filters.hideEmptyDays || viewMode !== 'grid') return gridDates;
    return gridDates.filter(date =>
      gridViewTableGroupItems.some(group =>
        group.items.some(row => gridViewCellIsListViewTableItem(row[date]))
      )
    );
  }, [gridDates, gridViewTableGroupItems, filters.hideEmptyDays, viewMode]);

  const gridViewTableFields: GridViewTableFields[] = [
    { key: 'host', label: 'Rental / Host' },
    { key: 'time' },
    ...activeDates.map(date => ({
      key: date,
      label: isoToDateLocal(date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }),
    })),
  ];

  const gridViewTableTemplates: BubbleTableTemplate<GridViewTableItem>[] = [
    {
      key: 'host',
      tdProps: makeDefaultGridViewTemplateTdProps,
      render: ({ host, resource }) =>
        (host || resource) && (
          <div>
            {resource && <div>{resource}</div>}
            <div className={`${resource ? 'text-xs text-slate' : ''}`}>{host}</div>
          </div>
        ),
    },
    {
      key: 'time',
      tdProps: makeDefaultGridViewTemplateTdProps,
    },
    ...activeDates.map(date => ({
      key: date,
      tdProps: makeDefaultGridViewTemplateTdProps,
      render: (row: GridViewTableItem) => {
        const val = row[date];
        return val && gridViewCellIsListViewTableItem(val) ? (
          <BubbleProgressBar
            current={val.seatsTaken}
            max={val.capacity}
            striped={val.indirectSeatsTaken > 0}
            allowOverfill={val.allowOverfill}
            active={expandedRow?.rowKey === row.id && expandedRow?.date === date}
            className="cursor-pointer stretched-link"
            style={{ width: '80px' }}
            onClick={() => toggleExpandedRow(row.id, date)}
          />
        ) : null;
      },
    })),
  ];

  function makeDefaultGridViewTemplateTdProps(
    data: GridViewTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const isExpanded = expandedRow?.rowKey === data.id;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        isExpanded ? 'bg-bubblegum-lightest' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
    };
  }

  const reservationTableFields = makeReservationTableFields();

  reservationTableFields.splice(2, 0, {
    key: 'attendance',
    label: 'Attendance',
    behavior: BubbleTableFieldBehavior.Condense,
  });

  const reservationTableTemplates: BubbleTableTemplate<ReservationTableItem>[] = [
    ...makeReservationTableTemplates(),
    {
      key: 'attendance',
      render: data => (
        <BubbleCheckboxGroup
          value={data.attendance}
          variant={opt => (opt === 'attended' ? 'lime' : opt === 'no-show' ? 'crimson' : 'navy')}
          allowEmpty
          options={['attended', 'no-show'] as ReservationAttendance[]}
          isSingleMode
          labelExtractor={opt => opt?.toUpperCase() || ''}
          readOnly={!canManageReservation(user, data._reservation)}
          optionAs="button"
          size="small"
          containerClassName="gap-1-i"
          className="p-1-i uppercase font-bold-i"
          onValue={s => handleChangeResAttendance(data._reservation, s)}
        />
      ),
    },
    {
      key: 'actions',
      render: data => (
        <BubbleOverlay loading={changingStatus} hideSpinner loadingClassName="rounded">
          <BubbleButton
            onClick={() => handleEditReservationById(data.sourceReservationId)}
            className="p-2-i"
            variant="navy"
            inverse
            icon="edit"
          />
          {canManageReservation(user, data._reservation) && (
            <>
              {!showDelete[data.id] && (
                <BubbleButton
                  className="p-2-i"
                  variant="dirt"
                  inverse
                  onClick={() => {
                    setShowDelete({ ...showDelete, [data.id]: true });
                  }}
                  icon="trash-alt"
                />
              )}
              {showDelete[data.id] && (
                <>
                  <BubbleButton
                    className="p-2-i"
                    variant="dirt"
                    inverse
                    onClick={() => handleToggleReservation(data._reservation)}
                    icon="trash-check"
                  />
                  <BubbleButton
                    className="p-2-i"
                    variant="slate"
                    inverse
                    onClick={() => {
                      setShowDelete({ ...showDelete, [data.id]: false });
                    }}
                    icon="trash-xmark"
                  />
                </>
              )}
            </>
          )}
        </BubbleOverlay>
      ),
    },
  ];

  const tallyTableFields: BubbleTableFields<ReservationTallyItem>[] = [
    { key: 'name' },
    { key: 'total' },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
  ];

  function tallyTableTemplates(
    setTally: React.Dispatch<React.SetStateAction<string | undefined>>
  ): BubbleTableTemplate<ReservationTallyItem>[] {
    return [
      {
        key: 'name',
        tdProps: makeDefaultTallyTemplateTdProps,
      },
      {
        key: 'total',
        render: item => (
          <BubbleProgressBar current={item.total} max={item.typeTotal} linearFill="ocean" />
        ),
        tdProps: makeDefaultTallyTemplateTdProps,
      },
      {
        key: 'actions',
        tdProps: makeDefaultTallyTemplateTdProps,
        render: item => (
          <>
            <BubbleButton
              variant="navy"
              inverse
              className="p-0-i"
              icon={item.isExpanded ? 'chevron-up' : 'chevron-down'}
              onClick={item.isExpanded ? () => setTally(undefined) : () => setTally(item.id)}
            />
          </>
        ),
      },
    ];
  }

  function makeDefaultTallyTemplateTdProps(item: ReservationTallyItem) {
    return {
      className: `${item.isExpanded ? 'bg-bubblegum-lightest' : ''}`,
    };
  }

  const [expandedTicketTallyKey, setExpandedTicketTallyKey] = useState<string | undefined>(
    undefined
  );
  const [expandedPackageTallyKey, setExpandedPackageTallyKey] = useState<string | undefined>(
    undefined
  );
  const [expandedAddonTallyKey, setExpandedAddonTallyKey] = useState<string | undefined>(undefined);

  function tallyTableItems(
    type: 'addons' | 'tickets' | 'packages',
    analytics: ReservationAnalytics
  ): ReservationTallyItem[] {
    switch (type) {
      case 'tickets':
        return analytics.tally.tickets
          .map(t => ({
            id: t.id,
            name: t.name,
            total: t.total,
            typeTotal: analytics.numOfTickets,
            isExpanded: expandedTicketTallyKey === t.id,
            _tally: t,
          }))
          .sort((a, b) => b.total - a.total);
      case 'addons':
        return analytics.tally.addons
          .map(t => ({
            id: t.id,
            name: t.name,
            total: t.total,
            typeTotal: analytics.numOfAddons,
            isExpanded: expandedAddonTallyKey === t.id,
            _tally: t,
          }))
          .sort((a, b) => b.total - a.total);

      case 'packages':
        return analytics.tally.packages
          .map(t => ({
            id: t.id,
            name: t.name,
            total: t.total,
            typeTotal: analytics.numOfPackages,
            isExpanded: expandedPackageTallyKey === t.id,
            _tally: t,
          }))
          .sort((a, b) => b.total - a.total);
    }
  }

  function tallyTableDetailsRender(item: ReservationTallyItem) {
    return (
      <table className="w-full border shadow-sm">
        <tbody>
          {item._tally.lines.map((res, i) => (
            <tr key={i}>
              <td className="p-1 w-0">{res.quantity}</td>
              <td className="p-1">{res.customer}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function handleSubmitReservation(newReservation: Reservation) {
    fetchData().catch(console.error);
    handleOpenReservationPanel({
      type: 'edit',
      edit: newReservation,
      goToPayments: true,
    });
  }

  async function handleToggleReservation(reservation: Reservation) {
    setChangingStatus(true);
    try {
      const newRes = await toggleStatus(reservation);
      setShowDelete({ ...showDelete, [reservation._id]: false });

      const newOngoings = [...ongoings];

      // soft update reservation in table without refetching everything
      newOngoings.forEach(ongoing => {
        ongoing.events.forEach(ev => {
          ev.items.forEach(slot => {
            slot.reservations.forEach(res => {
              if (safeIdCompare(res._id, newRes._id)) {
                res.status = newRes.status;
              }
            });
          });
        });
      });

      setOngoings(newOngoings);
    } finally {
      setChangingStatus(false);
    }
  }

  async function handleToggleException(
    date: string,
    event: BareEventTemplate,
    slot: ReserveTimelineItem
  ) {
    setChangingException(true);
    try {
      await toggleSlotException(activeProperty._id, event._id, {
        date,
        ruleId: slot.ruleId,
        slotId: slot.slotId,
        resource: slot.resource,
      });
      await fetchData();
    } finally {
      setChangingException(false);
    }
  }

  async function handleChangeResAttendance(
    reservation: Reservation,
    newStatus: ReservationAttendance | undefined
  ) {
    const newOngoings = [...ongoings];

    // soft update reservation in table without refetching everything
    newOngoings.forEach(ongoing => {
      ongoing.events.forEach(ev => {
        ev.items.forEach(slot => {
          slot.reservations.forEach(res => {
            if (safeIdCompare(res._id, reservation._id)) {
              res.attendance = newStatus;
            }
          });
        });
      });
    });

    setOngoings(newOngoings);

    changeAttendance(reservation, newStatus);
  }

  function listViewTableDetailsRender(item: ListViewTableItem) {
    const ticketTallyTemplates = tallyTableTemplates(setExpandedTicketTallyKey);
    const packageTallyTemplates = tallyTableTemplates(setExpandedPackageTallyKey);
    const addonTallyTemplates = tallyTableTemplates(setExpandedAddonTallyKey);

    const ticketTallyItems = tallyTableItems('tickets', item.analytics);
    const packageTallyItems = tallyTableItems('packages', item.analytics);
    const addonTallyItems = tallyTableItems('addons', item.analytics);

    const matrix = item._resourceMatrix;
    const { columnOpts, rowOpts, cellMatrix } = matrix || buildResourceMatrix([]);
    const activeMatrixCell = activeMatrixCellCoord
      ? cellMatrix[activeMatrixCellCoord.rowKey]?.[activeMatrixCellCoord.colKey]
      : undefined;

    const shownReservations = !activeMatrixCell ? item.reservations : activeMatrixCell.reservations;

    const TIME_COLUMN_WIDTH_REM = 12;
    const SLOT_COLUMN_WIDTH_REM = 12;

    const tableWidth = columnOpts.length * SLOT_COLUMN_WIDTH_REM + TIME_COLUMN_WIDTH_REM;

    const resourceGridItems: ResourceMatrixItem[] = rowOpts.map(r => ({
      id: r.key,
      time: r.label,
      timeLabel: r.timeLabel,
      host: r.hostLabel,
      isExceptionDate: r.isExceptionDate,
      slot: Object.values(cellMatrix[r.key] ?? {})[0]?.slot,
      cells: cellMatrix[r.key],
    }));

    function makeDefaultGridTemplateTdProps(
      _: ResourceMatrixItem,
      fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
    ) {
      return {
        ...fieldTdProps,
        className: [].filter(notUndefined).join(' '),
      };
    }

    const resourceGridFields: BubbleTableFields<ResourceMatrixItem>[] = [
      {
        key: 'timeHost',
        label: 'Time / Host',
        tdProps: {
          style: {
            width: `${TIME_COLUMN_WIDTH_REM}rem`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        },
        thProps: {
          style: {
            width: `${TIME_COLUMN_WIDTH_REM}rem`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        },
      },
      ...columnOpts.map(col => ({
        key: col.key,
        label: col.label,
        tdProps: {
          className: 'overflow-ellipsis',
          style: {
            width: `${SLOT_COLUMN_WIDTH_REM}rem`,
          },
        },
        thProps: {
          className: 'overflow-ellipsis',
          style: {
            width: `${SLOT_COLUMN_WIDTH_REM}rem`,
          },
        },
      })),
    ];

    const resourceGridTemplates: BubbleTableTemplate<ResourceMatrixItem>[] = [
      {
        key: 'timeHost',
        tdProps: makeDefaultGridTemplateTdProps,
        render: data => (
          <div>
            {data.timeLabel && <div>{data.timeLabel}</div>}
            <div className={`${data.timeLabel ? 'text-xs text-slate' : ''}`}>{data.time}</div>
            {data.host && <div className="text-xs text-slate">{data.host}</div>}
          </div>
        ),
      },
      ...columnOpts.map(col => ({
        key: col.key,
        tdProps: makeDefaultGridTemplateTdProps,
        render: (data: ResourceMatrixItem) => {
          const cell = data.cells[col.key];
          if (!cell) return undefined;

          const isActiveCell =
            activeMatrixCellCoord?.rowKey === data.id && activeMatrixCellCoord?.colKey === col.key;

          return (
            <div className="flex items-center">
              {cell.slot && canManageTimelineItem(user, cell.slot) && (
                <BubbleOverlay loading={changingException} hideSpinner loadingClassName="rounded">
                  <BubbleButton
                    onClick={() => handleToggleException(item.date, item._event, cell.slot!)}
                    className="p-2-i"
                    variant={cell.isExceptionDate ? 'crimson' : 'slate'}
                    inverse
                    icon="ban"
                  />
                </BubbleOverlay>
              )}
              <BubbleProgressBar
                current={cell.seatsTaken}
                allowOverfill={cell.allowOverfill}
                striped={cell.indirectSeatsTaken > 0}
                max={cell.capacity}
                active={isActiveCell}
                className="cursor-pointer flex-grow"
                onClick={() => {
                  setActiveMatrixCellCoord(prev =>
                    prev?.rowKey === data.id && prev?.colKey === col.key
                      ? undefined
                      : {
                          rowKey: data.id,
                          colKey: col.key,
                        }
                  );
                  setShowReservationList(true);
                }}
              />
            </div>
          );
        },
      })),
    ];

    return (
      <div
        className="grid gap-4 grid-cols-3 justify-items-start"
        style={{ maxWidth: `calc(100vw - 290px)`, position: 'sticky', left: '30px' }}
      >
        <div className="bg-white shadow-md w-full rounded overflow-hidden">
          <div className="px-3 py-2 bg-lime">
            <h1 className="font-bold uppercase text-xs text-white text-right">Ticket Summary</h1>
          </div>
          <BubbleTable
            responsive
            hideHeader
            hideShadow
            slim
            fields={tallyTableFields}
            templates={ticketTallyTemplates}
            items={ticketTallyItems.slice(0, showMoreTicketDetails ? undefined : 3)}
            detailsClassName="p-2 bg-bubblegum-lightest"
            detailsRender={tallyTableDetailsRender}
            detailsKey={expandedTicketTallyKey}
            onRowClick={subItem =>
              expandedTicketTallyKey === subItem.id
                ? setExpandedTicketTallyKey(undefined)
                : setExpandedTicketTallyKey(subItem.id)
            }
          />
          {!showMoreTicketDetails && ticketTallyItems.length > 3 && (
            <BubbleButton
              variant="lime"
              block
              inverse
              className="col-span-3"
              onClick={() => setShowMoreTicketDetails(true)}
              icon="eye"
            >
              Show more details
            </BubbleButton>
          )}
        </div>
        <div className="bg-white shadow-md w-full rounded overflow-hidden">
          <div className="px-3 py-2 bg-banana">
            <h1 className="font-bold uppercase text-xs text-white text-right">Package Summary</h1>
          </div>
          <BubbleTable
            responsive
            hideHeader
            hideShadow
            slim
            fields={tallyTableFields}
            templates={packageTallyTemplates}
            items={packageTallyItems.slice(0, showMorePackageDetails ? undefined : 3)}
            detailsClassName="p-2 bg-bubblegum-lightest"
            detailsRender={tallyTableDetailsRender}
            detailsKey={expandedPackageTallyKey}
            onRowClick={subItem =>
              expandedPackageTallyKey === subItem.id
                ? setExpandedPackageTallyKey(undefined)
                : setExpandedPackageTallyKey(subItem.id)
            }
          />
          {!showMorePackageDetails && packageTallyItems.length > 3 && (
            <BubbleButton
              variant="banana"
              block
              inverse
              className="col-span-3"
              onClick={() => setShowMorePackageDetails(true)}
              icon="eye"
            >
              Show more details
            </BubbleButton>
          )}
        </div>
        <div className="bg-white shadow-md w-full rounded overflow-hidden">
          <div className="px-3 py-2 bg-ocean">
            <h1 className="font-bold uppercase text-xs text-white text-right">Addon Summary</h1>
          </div>
          <BubbleTable
            responsive
            hideHeader
            hideShadow
            slim
            fields={tallyTableFields}
            templates={addonTallyTemplates}
            items={addonTallyItems.slice(0, showMoreAddonDetails ? undefined : 3)}
            detailsClassName="p-2 bg-bubblegum-lightest"
            detailsRender={tallyTableDetailsRender}
            detailsKey={expandedAddonTallyKey}
            onRowClick={subItem =>
              expandedAddonTallyKey === subItem.id
                ? setExpandedAddonTallyKey(undefined)
                : setExpandedAddonTallyKey(subItem.id)
            }
          />
          {!showMoreAddonDetails && addonTallyItems.length > 3 && (
            <BubbleButton
              variant="ocean"
              block
              inverse
              className="col-span-3"
              onClick={() => setShowMoreAddonDetails(true)}
              icon="eye"
            >
              Show more details
            </BubbleButton>
          )}
        </div>
        {matrix && (
          <div className="col-span-3 w-full overflow-x-auto p-1 -m-1">
            <BubbleTable
              fixed
              style={{ width: `${tableWidth}rem` }}
              headVariant="titanium"
              className="mx-auto"
              fields={resourceGridFields}
              items={resourceGridItems}
              templates={resourceGridTemplates}
            />
          </div>
        )}
        <div className="col-span-3 w-full">
          <div className="text-center relative mb-3">
            <BubbleButton
              variant="navy"
              inverse
              onClick={() => setShowReservationList(!showReservationList)}
              iconSuffix={showReservationList ? 'chevron-up' : 'chevron-down'}
            >
              {`${showReservationList ? 'Hide' : 'Show'} Reservations List`}
            </BubbleButton>
          </div>
          {showReservationList && (
            <div className="overflow-x-auto mt-2 p-1 -m-1 w-full mb-3">
              <BubbleTable
                responsive
                striped
                onRowClick={subItem => handleEditReservationById(subItem.sourceReservationId)}
                headVariant="slate"
                fields={reservationTableFields}
                templates={reservationTableTemplates}
                items={shownReservations}
                keyExtractor={item => {
                  const dateKey = dateToIsoUTC(item._reservation.date);
                  const base = `${item.id}-${dateKey}`;
                  return item.isIndirect ? `${base}-indirect` : base;
                }}
                showPretitle
                pretitleClassName="p-0-i"
                pretitleRender={() => (
                  <div className="px-4 pt-4 pb-1">
                    {activeMatrixCell && (
                      <div className="mb-2 flex items-center">
                        <BubbleButton
                          variant="white"
                          inverse
                          className="border border-white p-1-i mr-3"
                          icon="xmark"
                          onClick={() => {
                            setActiveMatrixCellCoord(undefined);
                          }}
                        >
                          {activeMatrixCell.description}
                        </BubbleButton>
                        <BubbleButton
                          variant="white"
                          icon="plus"
                          className="p-1-i ml-auto"
                          inverse
                          onClick={() => {
                            handleOpenReservationPanel({
                              type: 'create',
                              slot: activeMatrixCell.slot,
                              event: item._event,
                            });
                          }}
                        >
                          Add Reservation to This Slot
                        </BubbleButton>
                      </div>
                    )}
                    <BubbleInput
                      block
                      type="text"
                      variant="slate"
                      className="flex-grow"
                      placeholder="Search by name or reservation code..."
                      value={reservationSearch}
                      onValue={setReservationSearch}
                    />
                  </div>
                )}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  const experienceOpts = firstOf(
    ongoings.map(o => o.events.map(e => e.template)).flat(),
    e => e._id
  ).map(ev => ({
    key: ev._id,
    item: ev,
  }));

  const hostOpts = users.map(u => ({
    key: u._id,
    item: u,
  }));

  return (
    <PageWrapper {...props}>
      <Page className="Reservables absolute flex items-stretch">
        <MenuBar {...props} property={activeProperty} />
        <div className="SideMenu bg-turquoise-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={activeProperty.brand.logo} />
            <PropertyBanner {...props} property={activeProperty} swappable />
          </div>
          <div className={`flex flex-col relative items-stretch p-4 bg-turquoise-darker`}>
            <div className="grid gap-2">
              {canCreateSomeReservations(user) && (
                <BubbleButton
                  variant="lime"
                  className="text-left"
                  icon="plus"
                  onClick={() => {
                    handleOpenReservationPanel({
                      type: 'create',
                    });
                  }}
                >
                  Add Reservation
                </BubbleButton>
              )}
              <div className="flex items-stretch rounded-lg overflow-hidden bg-turquoise border border-turquoise-darkest">
                <div className="flex-grow px-4 py-2">
                  <BubbleDebounce onTrigger={[(date: string) => handleOnChangeDate(date), 500]}>
                    {({ debounce }) => (
                      <BubbleInput
                        type="date"
                        block
                        appearance="bare"
                        inputClassName="bg-turquoise text-white inverse-indicator"
                        value={queryStrings.date}
                        onValue={debounce}
                      />
                    )}
                  </BubbleDebounce>
                </div>
                {queryStrings.date !== DEFAULT_ONGOING_DATE && (
                  <BubbleButton
                    variant="turquoise"
                    appearance="squared"
                    className="-ml-4 pl-2-i"
                    icon="rotate-left"
                    onClick={() => handleOnChangeDate(DEFAULT_ONGOING_DATE)}
                  />
                )}
              </div>
              {!filters.selectedDayOnly && (
                <div
                  className="grid grid-cols-3 items-stretch rounded-lg overflow-hidden"
                  style={{ gridTemplateColumns: 'auto 1fr auto' }}
                >
                  <BubbleButton
                    block
                    variant="slate"
                    appearance="squared"
                    onClick={() => handleOnAddDaysBefore(7)}
                    icon="arrow-left"
                  />
                  <div className="flex items-center justify-center text-white font-semibold bg-slate px-1">
                    Load Days
                  </div>
                  <BubbleButton
                    block
                    variant="slate"
                    appearance="squared"
                    onClick={() => handleOnAddDaysAfter(7)}
                    icon="arrow-right"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 rounded-lg overflow-hidden border border-white">
                <BubbleButton
                  block
                  variant="white"
                  inverse={viewMode === 'grid'}
                  appearance="squared"
                  icon="list"
                  onClick={() => setViewMode('list')}
                >
                  List
                </BubbleButton>
                <BubbleButton
                  block
                  variant="white"
                  inverse={viewMode === 'list'}
                  appearance="squared"
                  icon={['fass', 'chart-tree-map']}
                  onClick={() => setViewMode('grid')}
                >
                  Grid
                </BubbleButton>
              </div>
              <BubbleButton
                variant="white"
                inverse
                className="text-left border border-white"
                icon="repeat"
                onClick={() => fetchData().catch(console.error)}
              >
                Refresh Data
              </BubbleButton>
            </div>
          </div>
          <BubbleAccordion
            variant="turquoise"
            className="flex-grow flex flex-col border-t border-turquoise-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4 py-4">
              <BubbleFormGroup label="Experiences" labelClassName="text-white-i" className="px-4">
                <BubbleTypeahead
                  block
                  variant="turquoise"
                  placeholder="Filter experiences shown..."
                  menuClassName="max-h-80 overflow-y-auto"
                  value={filters.selectedExperiences}
                  labelExtractor={p => p.title}
                  options={experienceOpts}
                  onAdd={v =>
                    setFilters({
                      ...filters,
                      selectedExperiences: [...filters.selectedExperiences, v],
                    })
                  }
                  onRemove={v =>
                    setFilters({
                      ...filters,
                      selectedExperiences: filters.selectedExperiences.filter(
                        v2 => v2.key !== v.key
                      ),
                    })
                  }
                  onClearAll={() => setFilters({ ...filters, selectedExperiences: [] })}
                />
                {viewMode === 'list' && (
                  <BubbleCheckbox
                    label="Group rows by event"
                    className="text-white mt-1"
                    variant="turquoise"
                    checked={filters.groupByEvent}
                    onValue={(checked: boolean) => {
                      setFilters({ ...filters, groupByEvent: checked });
                    }}
                  />
                )}
              </BubbleFormGroup>
              {canManageAllReservations(user) && (
                <BubbleFormGroup label="Host" labelClassName="text-white-i" className="px-4">
                  <BubbleTypeahead
                    block
                    variant="turquoise"
                    placeholder="Filter hosts shown..."
                    menuClassName="max-h-80 overflow-y-auto"
                    value={filters.selectedHosts}
                    options={hostOpts}
                    labelExtractor={p => (p ? makeFullName(p.firstName, p.lastName) : 'No Host')}
                    onAdd={v =>
                      setFilters({
                        ...filters,
                        selectedHosts: [...filters.selectedHosts, v],
                      })
                    }
                    onRemove={v =>
                      setFilters({
                        ...filters,
                        selectedHosts: filters.selectedHosts.filter(v2 => v2.key !== v.key),
                      })
                    }
                    onClearAll={() => setFilters({ ...filters, selectedHosts: [] })}
                  />
                </BubbleFormGroup>
              )}
              <BubbleFormGroup label="View Options" labelClassName="text-white-i" className="px-4">
                {viewMode === 'list' && (
                  <>
                    <BubbleCheckbox
                      label="Selected day only"
                      className="text-white mt-1"
                      variant="turquoise"
                      checked={filters.selectedDayOnly}
                      onValue={(checked: boolean) => {
                        setFilters({ ...filters, selectedDayOnly: checked });
                      }}
                    />
                    <BubbleCheckbox
                      label="Hide empty slots"
                      className="text-white mt-1"
                      variant="turquoise"
                      checked={filters.hideEmptySlots}
                      onValue={(checked: boolean) => {
                        setFilters({ ...filters, hideEmptySlots: checked });
                      }}
                    />
                  </>
                )}
                <BubbleCheckbox
                  label="Hide empty days"
                  className="text-white mt-1"
                  variant="turquoise"
                  checked={filters.hideEmptyDays}
                  onValue={(checked: boolean) => {
                    setFilters({ ...filters, hideEmptyDays: checked });
                  }}
                />
                <BubbleCheckbox
                  label="Include cancelled reservations"
                  className="text-white mt-1"
                  variant="turquoise"
                  checked={filters.showArchived}
                  onValue={checked => {
                    setFilters({ ...filters, showArchived: checked });
                  }}
                />
                <BubbleCheckbox
                  label="Include indirect reservations"
                  className="text-white mt-1"
                  variant="turquoise"
                  checked={filters.showIndirect}
                  onValue={checked => {
                    setFilters({ ...filters, showIndirect: checked });
                  }}
                />
              </BubbleFormGroup>
            </div>
          </BubbleAccordion>
        </div>
        <div className="ReservablesContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="ReservablesHeader align-with-logo flex items-end px-5 sticky top-0
              left-0 z-10 bg-blue-lightest"
          >
            <div className="mb-3 pl-3 flex-grow flex items-end">
              <ReserveNavigation active="reserve" />
            </div>
            <div
              className="absolute w-full bg-blue-lightest flex border-blue-lightest -mx-5 px-5"
              style={{
                height: 'calc(40px + 0.75rem)',
                bottom: 'calc(-40px - 0.75rem)',
                borderBottomWidth: '0.75rem',
                zIndex: 10,
              }}
            >
              <ReserveSubNavigation active="calendar" property={activeProperty} />
            </div>
          </div>
          <div className="ReservablesBody mx-5 content-minus-logo-minus-bar min-w-900">
            <div
              style={{
                marginTop: 'calc(40px + 0.75rem)',
              }}
            >
              <BubbleOverlay loading={loading} loadingMinHeight="medium">
                {viewMode === 'list' ? (
                  <div>
                    {listViewTableGroupItems.map(bti => (
                      <BubbleTable
                        key={bti.id}
                        className="mb-3"
                        responsive
                        striped
                        headVariant="turquoise"
                        fields={listViewTableFields}
                        items={bti.items}
                        templates={listViewTableTemplates}
                        keyExtractor={item => item.id}
                        stickyHeader={182}
                        onRowClick={item => {
                          toggleExpandedRow(item.id, bti.date);
                        }}
                        detailsKey={expandedRow?.rowKey}
                        detailsRender={listViewTableDetailsRender}
                        detailsClassName="bg-bubblegum-lightest"
                        showPretitle
                        pretitleClassName="p-0-i"
                        pretitleRender={() => (
                          <div
                            className="px-5 py-3 font-bold text-white text-center"
                            style={{ backgroundColor: bti.color }}
                          >
                            {bti.date}
                          </div>
                        )}
                      />
                    ))}
                    {!listViewTableGroupItems.length && !loading && (
                      <BubbleAlert variant="banana">No reservations to show</BubbleAlert>
                    )}
                    <div className="mt-3">&nbsp;</div>
                  </div>
                ) : (
                  <div>
                    {gridViewTableGroupItems.map(bti => (
                      <BubbleTable
                        key={bti.id}
                        className="mb-3"
                        striped
                        headVariant="turquoise"
                        fields={gridViewTableFields}
                        items={bti.items}
                        templates={gridViewTableTemplates}
                        detailsKey={expandedRow?.rowKey}
                        detailsRender={item => {
                          const val = expandedRow && item[expandedRow.date];
                          if (gridViewCellIsListViewTableItem(val)) {
                            return listViewTableDetailsRender(val);
                          }
                          return undefined;
                        }}
                        detailsClassName="bg-bubblegum-lightest"
                        keyExtractor={item => item.id}
                        stickyHeader={182}
                        showPretitle
                        pretitleClassName="p-0-i"
                        pretitleRender={() => (
                          <div
                            className="px-5 py-3 font-bold text-white text-center"
                            style={{ backgroundColor: bti.color }}
                          >
                            {bti.name}
                          </div>
                        )}
                      />
                    ))}
                    {!gridViewTableGroupItems.length && !loading && (
                      <BubbleAlert variant="banana">No reservations to show</BubbleAlert>
                    )}
                    <div className="mt-3">&nbsp;</div>
                  </div>
                )}
              </BubbleOverlay>
            </div>
          </div>
        </div>
        {mode && (
          <BubbleDrawer containerClassName="bg-eggshell" onClose={handleCloseReservationPanel}>
            <ReservationPath
              mode={mode}
              users={users}
              onClose={handleCloseReservationPanel}
              onRefreshReservation={handleSubmitReservation}
              onNavigateToReservation={r => handleOpenReservationPanel({ type: 'edit', edit: r })}
            />
          </BubbleDrawer>
        )}
      </Page>
    </PageWrapper>
  );
}
